import type { ContractListType, OutSideContractItem } from '@/api/rights/types';
import { env, urlSyncLocationOrigin } from '@/constant';

/**
 * 协议pdf预览地址
 * 1. pro环境下不拼接env字段
 * 2. 因为小程序(微信)环境下，只允许m2.hellobike.com域名访问，所以在此修正域名
 */
const CONTRACTS_URL_BASE = urlSyncLocationOrigin('https://m.hellobike.com/AppFinanceContracts');
const generateUrl = (envType: string) => `${CONTRACTS_URL_BASE}${envType === 'pro' ? '' : `/${envType}`}/latest/pdf_preview/index.html`;
const contractsViewUrl = generateUrl(env);

/**
 * 使用资源预加载技术提前加载PDF文档
 *
 * 采用<link rel="prefetch">标准实现方式，比锚点标签方案更符合规范且不会污染DOM结构
 * 推荐在浏览器空闲时间预加载关键资源（兼容现代浏览器）
 *
 * @param pdfUrl - 需要预加载的PDF完整地址，需确保同源或配置CORS
 * @param as - 显式声明资源类型（默认'fetch'，PDF建议用'document'）
 */
const prefetchPdfResource = (pdfUrl: string, as: 'fetch' | 'document' = 'document'): void => {
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = pdfUrl;
  link.as = as;

  // 添加跨域请求处理（当资源在不同域名时必需）
  if (new URL(pdfUrl).origin !== window.location.origin) {
    link.crossOrigin = 'anonymous';
  }

  document.head.appendChild(link);
};

/** 处理合同详情
 * @param detail - 合同详情
 * @returns - 处理后的合同详情
 * @remark - 处理合同预加载，提升用户体验
 */
export const processDetails = (detail: ContractListType): {
  name: string;
  contentUrl: string;
} => {
  prefetchPdfResource(detail.contractUrl);
  return {
    name: detail.contractName,
    contentUrl: `${contractsViewUrl}?pdf_url=${detail.contractUrl}`,
  };
};

/** 处理卖量合同详情
 * @param detail - 合同详情
 * @returns - 处理后的合同详情
 * @remark - 处理合同预加载，提升用户体验
 */
export const processSellFlowDetailsList = (
  detail: OutSideContractItem,
): { name: string; contentUrl: string } => {
  prefetchPdfResource(detail.contentUrl);
  return {
    name: detail.name,
    contentUrl: `${contractsViewUrl}?pdf_url=${detail.contentUrl}`,
  };
};
